import React, { useState } from "react";
import api from "../lib/api";
import { X, Check } from "lucide-react";
import { toast } from "sonner";

export default function WaitlistModal({ tenant, label = "Join the waitlist", onClose }) {
    const [form, setForm] = useState({ name: "", email: "", company: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await api.post("/waitlist", { ...form, tenant });
            setDone(true);
        } catch (ex) {
            toast.error(ex.response?.data?.detail || "Submission failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid={`waitlist-modal-${tenant}`}
        >
            <div
                className="glass rounded-2xl p-8 max-w-md w-full relative"
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
                    data-testid="waitlist-close"
                >
                    <X size={18} />
                </button>

                {done ? (
                    <div className="text-center py-6" data-testid="waitlist-success">
                        <div className="w-14 h-14 mx-auto rounded-full bg-[hsl(40_61%_25%)] flex items-center justify-center text-[hsl(45_85%_68%)] mb-4">
                            <Check size={28} />
                        </div>
                        <div className="font-heading text-2xl">On the list.</div>
                        <p className="text-sm text-muted-foreground mt-2">
                            You'll be the first to know when this vertical goes live.
                            Check your inbox for confirmation.
                        </p>
                        <button
                            onClick={onClose}
                            className="mt-6 px-6 py-2.5 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors"
                            data-testid="waitlist-close-success"
                        >
                            Close
                        </button>
                    </div>
                ) : (
                    <form onSubmit={submit} className="space-y-3" data-testid="waitlist-form">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.3em] text-[hsl(45_85%_68%)]">{tenant.toUpperCase()}</div>
                            <div className="font-heading text-2xl mt-1">{label}</div>
                            <p className="text-sm text-muted-foreground mt-1">
                                Early access, no commitment. We'll loop you in when the signal goes live.
                            </p>
                        </div>

                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Name</label>
                            <input
                                required
                                value={form.name}
                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                data-testid="waitlist-name"
                                className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Email</label>
                            <input
                                required
                                type="email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                data-testid="waitlist-email"
                                className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Company (optional)</label>
                            <input
                                value={form.company}
                                onChange={(e) => setForm({ ...form, company: e.target.value })}
                                data-testid="waitlist-company"
                                className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm"
                            />
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">What are you hoping to do? (optional)</label>
                            <textarea
                                rows={3}
                                maxLength={500}
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                data-testid="waitlist-notes"
                                className="mt-1 w-full px-4 py-2.5 rounded-md bg-[hsl(151_55%_9%)] border border-[hsl(40_40%_25%/0.3)] focus:outline-none focus:ring-2 focus:ring-[hsl(40_61%_53%)] text-sm resize-none"
                            />
                        </div>

                        <button
                            type="submit"
                            disabled={busy}
                            data-testid="waitlist-submit"
                            className="w-full py-3 rounded-md bg-[hsl(40_61%_53%)] text-[hsl(40_20%_10%)] font-semibold hover:bg-[hsl(45_75%_62%)] transition-colors disabled:opacity-50"
                        >
                            {busy ? "Submitting..." : "Reserve your seat"}
                        </button>
                    </form>
                )}
            </div>
        </div>
    );
}
